// Tiles of numbers (R-AN1, R-E8): each tile asks the server for one metric of the area and the period and shows the value, the previous
// period when it is asked, or the reason the number is not given. The section «Аналитика» and the summary of a record share them.

import { formatMoney, type Id } from '@confdeck/core-shared';
import { cx } from '../ui/primitives.tsx';
import { useMetric, type CatalogMetric, type MetricAnswer, type Period } from './api.ts';
import s from './analytics.module.css';

export const metricText = (unit: MetricAnswer['unit'], v: number) => (unit === 'money' ? formatMoney(v) : v.toLocaleString('ru-RU'));

function Tile({ m, scope, period, compare }: { m: CatalogMetric; scope: Id | null; period: Period; compare: boolean }) {
  const { answer, error, loading } = useMetric({ metric: m.key, scope: scope ? { recordId: scope } : null, period, ...(compare && period !== 'all' ? { compare: 'previous' as const } : {}) });
  const prev = answer?.previous;
  const delta = answer && prev ? answer.value - prev.value : 0;
  return (
    <article className={s.tile} data-metric={m.key} title={m.description}>
      <h3 className={s.name}>{m.name}</h3>
      {period !== 'all' && <p className={s.basis}>{m.basis}</p>}
      {error ? <p className={s.error} role="note">{error}</p> : (
        <p className={s.value} data-loading={loading || undefined} aria-live="polite">{answer ? metricText(answer.unit, answer.value) : '…'}</p>
      )}
      {prev && (
        <p className={s.prev}>
          Прошлый период: {metricText(answer!.unit, prev.value)}
          {delta !== 0 && <span className={cx(delta > 0 ? s.up : s.down)}> ({delta > 0 ? '+' : '−'}{metricText(answer!.unit, Math.abs(delta))})</span>}
        </p>
      )}
    </article>
  );
}

/** `metrics` — in the order to show; `scope` — the record of the area (null — the whole account). */
export function MetricTiles({ metrics, scope, period, compare = false, label }: { metrics: CatalogMetric[]; scope: Id | null; period: Period; compare?: boolean; label?: string }) {
  if (!metrics.length) return <p className={s.empty}>Для этой области нет чисел, которые вам доступны.</p>;
  return (
    <div className={s.tiles} aria-label={label}>
      {metrics.map((m) => <Tile key={m.key} m={m} scope={scope} period={period} compare={compare} />)}
    </div>
  );
}
